
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Link,
    useLocation,
    useNavigate,
} from "react-router-dom";
import {
    ArrowRight,
    Check,
    ChevronDown,
    ChevronRight,
    Heart,
    Hotel,
    MapPin,
    Search,
    SlidersHorizontal,
    Star,
    RotateCcw,
    X,
    ImageOff,
    LayoutGrid,
    List as ListIcon,
    ShieldCheck,
    Sparkles,
    Sliders,
} from "lucide-react";

import hotelImg from "../assests/images/hotel-bg.jpg";

// ==========================================
// CONFIGURATION
// ==========================================

const API_URL = "https://dubaibudgetivehotels.com/api";

const IMAGE_BASE =
    "https://uaediscountedhotels.com/uploads/hotels/";

const WISHLIST_KEY = "dubai_hotel_wishlist";
const LEGACY_KEY = "wishlist";

// ==========================================
// HELPERS
// ==========================================

const getArray = (data) => {
    if (Array.isArray(data)) return data;

    for (const item of [
        data?.data,
        data?.hotels,
        data?.destinations,
        data?.data?.data,
        data?.data?.hotels,
    ]) {
        if (Array.isArray(item)) return item;
    }

    return [];
};

const hotelName = (hotel) =>
    hotel?.name ||
    hotel?.hotel_name ||
    hotel?.title ||
    "Hotel in Dubai";

const hotelLocation = (hotel) => {
    const value =
        hotel?.location ||
        hotel?.address ||
        hotel?.area?.name ||
        hotel?.destination?.name ||
        hotel?.city;

    return typeof value === "string" && value.trim()
        ? value
        : "Dubai, UAE";
};

const hotelPrice = (hotel) => {
    const value =
        hotel?.price_per_night ??
        hotel?.starting_price ??
        hotel?.min_price ??
        hotel?.price;

    if (
        value === null ||
        value === undefined ||
        value === "" ||
        !Number.isFinite(Number(value))
    ) {
        return null;
    }

    return Number(value);
};

const hotelRating = (hotel) => {
    const value =
        hotel?.rating ?? hotel?.average_rating;

    if (
        value === null ||
        value === undefined ||
        value === "" ||
        !Number.isFinite(Number(value))
    ) {
        return null;
    }

    return Number(value);
};

const imageUrl = (hotel) => {
    const raw =
        hotel?.image ||
        hotel?.featured_image ||
        hotel?.thumbnail ||
        hotel?.cover_image ||
        hotel?.images?.[0];

    const image =
        typeof raw === "object"
            ? raw?.url || raw?.path || raw?.image
            : raw;

    if (typeof image !== "string" || !image.trim()) {
        return hotelImg;
    }

    if (/^https?:\/\//i.test(image)) return image;

    if (image.startsWith("//")) {
        return `https:${image}`;
    }

    if (image.startsWith("/")) return image;

    return `${IMAGE_BASE}${image}`;
};

const plainText = (value) =>
    String(value || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();

const formatAED = (value) =>
    new Intl.NumberFormat("en-AE", {
        maximumFractionDigits: 0,
    }).format(value);

const hotelKey = (hotel) =>
    String(hotel?.id ?? hotel?.slug ?? "");

const readStored = (key) => {
    try {
        const value = JSON.parse(
            localStorage.getItem(key) || "[]"
        );

        return Array.isArray(value) ? value : [];
    } catch {
        return [];
    }
};

const matchesHotel = (saved, hotel) => {
    const id = hotelKey(hotel);

    const savedId =
        typeof saved === "object"
            ? String(saved?.id ?? "")
            : String(saved);

    return (
        Boolean(id && savedId === id) ||
        Boolean(
            saved?.slug &&
            hotel?.slug &&
            saved.slug === hotel.slug
        )
    );
};

const readWishlist = () => {
    const items = [
        ...readStored(WISHLIST_KEY),
        ...readStored(LEGACY_KEY),
    ];

    return items.filter(
        (item, index) =>
            items.findIndex((other) =>
                matchesHotel(other, item)
            ) === index
    );
};

// ==========================================
// HOTEL CARD
// ==========================================

function HotelCard({
    hotel,
    saved,
    onWishlist,
    view = "grid",
}) {
    const [imageFailed, setImageFailed] = useState(false);

    const price = hotelPrice(hotel);
    const rating = hotelRating(hotel);
    const name = hotelName(hotel);
    const destination = hotelLocation(hotel);

    const description = plainText(
        hotel?.short_description || hotel?.description
    );

    const href = hotel?.slug
        ? `/hotels/${hotel.slug}`
        : "/hotels";

    const horizontal = view === "list";
    const src = imageUrl(hotel);

    useEffect(() => {
        setImageFailed(false);
    }, [src]);

    return (
        <article
            className={`
        group overflow-hidden
        rounded-[18px]
        border border-[#E2ECE6]
        bg-white
        shadow-[0_4px_20px_rgba(6,78,59,0.04)]
        transition-all duration-300
        hover:-translate-y-1
        hover:border-[#B9DEC9]
        hover:shadow-[0_16px_36px_rgba(6,78,59,0.10)]
        ${horizontal ? "md:flex" : ""}
      `}
        >
            {/* HOTEL IMAGE */}
            <div
                className={`
          relative shrink-0 overflow-hidden
          bg-[#E8F1EC]
          ${horizontal
                        ? "h-[240px] md:h-auto md:w-[38%]"
                        : "h-[225px] sm:h-[240px]"
                    }
        `}
            >
                <Link
                    to={href}
                    className="absolute inset-0"
                    aria-label={`View ${name}`}
                >
                    <img
                        src={imageFailed ? hotelImg : src}
                        onError={() => setImageFailed(true)}
                        alt={name}
                        loading="lazy"
                        className="
              h-full w-full object-cover
              transition-transform duration-700
              group-hover:scale-105
            "
                    />

                    <div
                        className="
              absolute inset-0
              bg-gradient-to-t
              from-[#064E3B]/45
              via-transparent
              to-black/10
            "
                    />
                </Link>

                <span
                    className="
            pointer-events-none absolute
            left-4 top-4
            rounded-full bg-white/95
            px-3 py-1.5
            text-[10px] font-extrabold
            uppercase tracking-[0.1em]
            text-[#168B43]
            shadow-sm
          "
                >
                    Dubai Stay
                </span>

                {/* WISHLIST */}
                <button
                    type="button"
                    onClick={() => onWishlist(hotel)}
                    aria-label={
                        saved
                            ? `Remove ${name} from wishlist`
                            : `Save ${name} to wishlist`
                    }
                    aria-pressed={saved}
                    className="
            absolute right-4 top-4 z-10
            flex h-10 w-10 items-center
            justify-center rounded-full
            bg-white shadow-md
            transition hover:scale-110
            focus-visible:outline
            focus-visible:outline-2
            focus-visible:outline-[#168B43]
          "
                >
                    <Heart
                        size={19}
                        className={
                            saved
                                ? "fill-[#E64B69] text-[#E64B69]"
                                : "text-[#17324D]"
                        }
                    />
                </button>

                {/* RATING */}
                {rating !== null && (
                    <div
                        className="
              absolute bottom-4 left-4
              flex items-center gap-1.5
              rounded-[9px] bg-white
              px-3 py-2 shadow-md
            "
                    >
                        <Star
                            size={14}
                            className="
                fill-[#F3AE2C]
                text-[#F3AE2C]
              "
                        />

                        <span
                            className="
                text-[12px] font-extrabold
                text-[#17324D]
              "
                        >
                            {rating.toFixed(1)}
                        </span>

                        <span className="text-[11px] text-[#94A3B8]">
                            / 5
                        </span>
                    </div>
                )}
            </div>

            {/* HOTEL DETAILS */}
            <div
                className="
          flex min-w-0 flex-1 flex-col
          justify-between p-5 sm:p-6
        "
            >
                <div>
                    <div
                        className="
              mb-2 flex items-center
              gap-1.5 text-[12px]
              text-[#64748B]
            "
                    >
                        <MapPin
                            size={14}
                            className="shrink-0 text-[#168B43]"
                        />

                        <span className="truncate">
                            {destination}
                        </span>
                    </div>

                    <Link to={href}>
                        <h3
                            className="
                line-clamp-2 text-[19px]
                font-extrabold leading-[1.4]
                tracking-[-0.025em]
                text-[#17324D]
                transition-colors
                group-hover:text-[#168B43]
                sm:text-[21px]
              "
                        >
                            {name}
                        </h3>
                    </Link>

                    <p
                        className="
              mt-3 line-clamp-2
              min-h-[42px]
              text-[13px] leading-[22px]
              text-[#718096]
            "
                    >
                        {description ||
                            "Explore this hotel and discover accommodation options for your Dubai stay."}
                    </p>
                </div>

                {/* PRICE AND BUTTON */}
                <div
                    className="
            mt-5 flex items-end
            justify-between gap-3
            border-t border-[#EAF1EC]
            pt-4
          "
                >
                    <div>
                        {price !== null ? (
                            <>
                                <p
                                    className="
                    text-[10px] font-bold
                    uppercase tracking-[0.1em]
                    text-[#94A3B8]
                  "
                                >
                                    From / Night
                                </p>

                                <div className="mt-1 flex items-baseline gap-1">
                                    <span
                                        className="
                      text-[13px] font-bold
                      text-[#168B43]
                    "
                                    >
                                        AED
                                    </span>

                                    <span
                                        className="
                      text-[25px] font-extrabold
                      tracking-[-0.04em]
                      text-[#17324D]
                    "
                                    >
                                        {formatAED(price)}
                                    </span>
                                </div>
                            </>
                        ) : (
                            <p
                                className="
                  text-[13px] font-semibold
                  text-[#64748B]
                "
                            >
                                View rates
                            </p>
                        )}
                    </div>

                    <Link
                        to={href}
                        className="
              inline-flex shrink-0
              items-center justify-center gap-2
              rounded-[10px]
              bg-[#168B43]
              px-4 py-3
              text-[12px] font-bold
              text-white
              transition-all duration-200
              hover:bg-[#087D37]
            "
                    >
                        View Hotel
                        <ArrowRight size={15} />
                    </Link>
                </div>
            </div>
        </article>
    );
}

// ==========================================
// FILTER SIDEBAR
// ==========================================

function FilterPanel({
    destinations,
    selectedDestinations,
    toggleDestination,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    rating,
    setRating,
    clearFilters,
    onClose,
}) {
    const selected = selectedDestinations.map((d) =>
        String(d.id)
    );

    return (
        <aside
            className="
        overflow-hidden rounded-[18px]
        border border-[#E2ECE6]
        bg-white
        shadow-[0_5px_22px_rgba(6,78,59,0.04)]
      "
        >
            {/* FILTER HEADER */}
            <div
                className="
          border-b border-[#E8F0EA]
          bg-[#F5FAF7] px-5 py-5
        "
            >
                <div className="flex items-center justify-between">
                    <div>
                        <p
                            className="
                mb-1 text-[10px]
                font-extrabold uppercase
                tracking-[0.16em]
                text-[#168B43]
              "
                        >
                            Refine Your Search
                        </p>

                        <h2
                            className="
                flex items-center gap-2
                text-[20px] font-extrabold
                text-[#17324D]
              "
                        >
                            <SlidersHorizontal
                                size={19}
                                className="text-[#168B43]"
                            />

                            Filters
                        </h2>
                    </div>

                    {onClose && (
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close filters"
                            className="
                flex h-9 w-9 items-center
                justify-center rounded-full
                bg-white text-[#17324D]
              "
                        >
                            <X size={19} />
                        </button>
                    )}
                </div>
            </div>

            <div className="p-5">
                {/* DESTINATIONS */}
                <div>
                    <h3
                        className="
              mb-4 flex items-center gap-2
              text-[13px] font-extrabold
              text-[#17324D]
            "
                    >
                        <MapPin
                            size={17}
                            className="text-[#168B43]"
                        />
                        Destinations
                    </h3>

                    <div
                        className="
              max-h-[240px] space-y-3
              overflow-y-auto pr-1
            "
                    >
                        {destinations.length ? (
                            destinations.map((destination) => (
                                <label
                                    key={destination.id}
                                    className="
                    flex cursor-pointer
                    items-center gap-3
                    text-[12px] text-[#64748B]
                    transition hover:text-[#168B43]
                  "
                                >
                                    <input
                                        type="checkbox"
                                        checked={selected.includes(
                                            String(destination.id)
                                        )}
                                        onChange={() =>
                                            toggleDestination(destination)
                                        }
                                        className="
                      h-4 w-4 cursor-pointer
                      accent-[#168B43]
                    "
                                    />

                                    <span>
                                        {destination.name ||
                                            destination.title ||
                                            "Destination"}
                                    </span>
                                </label>
                            ))
                        ) : (
                            <p className="text-xs text-[#94A3B8]">
                                No destination options available.
                            </p>
                        )}
                    </div>
                </div>

                <div className="my-6 h-px bg-[#EAF1EC]" />

                {/* PRICE FILTER */}
                <div>
                    <h3
                        className="
              mb-4 text-[13px]
              font-extrabold text-[#17324D]
            "
                    >
                        Price Per Night
                        <span
                            className="
                ml-1 font-normal
                text-[#94A3B8]
              "
                        >
                            (AED)
                        </span>
                    </h3>

                    <div className="grid grid-cols-2 gap-3">
                        <label>
                            <span
                                className="
                  mb-2 block text-[11px]
                  text-[#64748B]
                "
                            >
                                Min Price
                            </span>

                            <input
                                type="number"
                                min="0"
                                value={minPrice}
                                onChange={(e) =>
                                    setMinPrice(
                                        Math.max(0, Number(e.target.value) || 0)
                                    )
                                }
                                className="
                  w-full rounded-[10px]
                  border border-[#E2ECE6]
                  bg-[#F8FBF9]
                  px-3 py-3 text-[13px]
                  font-semibold text-[#17324D]
                  outline-none
                  focus:border-[#168B43]
                "
                            />
                        </label>

                        <label>
                            <span
                                className="
                  mb-2 block text-[11px]
                  text-[#64748B]
                "
                            >
                                Max Price
                            </span>

                            <input
                                type="number"
                                min="0"
                                value={maxPrice}
                                onChange={(e) =>
                                    setMaxPrice(
                                        Math.max(0, Number(e.target.value) || 0)
                                    )
                                }
                                className="
                  w-full rounded-[10px]
                  border border-[#E2ECE6]
                  bg-[#F8FBF9]
                  px-3 py-3 text-[13px]
                  font-semibold text-[#17324D]
                  outline-none
                  focus:border-[#168B43]
                "
                            />
                        </label>
                    </div>
                </div>

                <div className="my-6 h-px bg-[#EAF1EC]" />

                {/* RATING FILTER */}
                <div>
                    <h3
                        className="
              mb-4 flex items-center gap-2
              text-[13px] font-extrabold
              text-[#17324D]
            "
                    >
                        <Star
                            size={17}
                            className="text-[#F3AE2C]"
                        />
                        Guest Rating
                    </h3>

                    <div className="space-y-2">
                        {[0, 3, 4, 4.5].map((value) => (
                            <button
                                key={value}
                                type="button"
                                onClick={() => setRating(value)}
                                className={`
                  flex w-full items-center
                  justify-between rounded-[10px]
                  border px-3.5 py-3
                  text-left text-[12px]
                  font-semibold transition
                  ${rating === value
                                        ? "border-[#168B43] bg-[#EAF8EF] text-[#168B43]"
                                        : "border-[#E8F0EA] text-[#64748B] hover:bg-[#F5FAF7]"
                                    }
                `}
                            >
                                <span className="flex items-center gap-2">
                                    {value ? (
                                        <>
                                            <Star
                                                size={14}
                                                className="
                          fill-[#F3AE2C]
                          text-[#F3AE2C]
                        "
                                            />
                                            {value}+ Rating
                                        </>
                                    ) : (
                                        "Any Rating"
                                    )}
                                </span>

                                {rating === value && (
                                    <Check
                                        size={16}
                                        className="text-[#168B43]"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* RESET FILTERS */}
                <button
                    type="button"
                    onClick={clearFilters}
                    className="
            mt-7 flex w-full
            items-center justify-center gap-2
            rounded-[10px]
            border border-[#CFE6D8]
            bg-[#F5FAF7]
            px-4 py-3
            text-[12px] font-bold
            text-[#168B43]
            transition hover:bg-[#E8F7EE]
          "
                >
                    <RotateCcw size={15} />
                    Reset Filters
                </button>

                {/* MOBILE APPLY */}
                {onClose && (
                    <button
                        type="button"
                        onClick={onClose}
                        className="
              mt-3 w-full rounded-[10px]
              bg-[#168B43] py-3.5
              text-[13px] font-bold
              text-white
              hover:bg-[#087D37]
            "
                    >
                        Show Hotels
                    </button>
                )}
            </div>
        </aside>
    );
}

// ==========================================
// MAIN HOTEL LIST
// ==========================================

export default function HotelList({
    limit,
    showViewAllButton = false,
    showSidebar = true,
    showBanner = false,
    showHeading = false,
}) {
    const navigate = useNavigate();
    const location = useLocation();

    const [hotels, setHotels] = useState([]);
    const [destinations, setDestinations] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [retry, setRetry] = useState(0);

    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] =
        useState("");

    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(5000);
    const [rating, setRating] = useState(0);

    const [
        selectedDestinations,
        setSelectedDestinations,
    ] = useState([]);

    const [
        mobileFiltersOpen,
        setMobileFiltersOpen,
    ] = useState(false);

    const [sort, setSort] = useState("recommended");
    const [view, setView] = useState("grid");

    const [wishlist, setWishlist] =
        useState(readWishlist);

    const shouldShowHeading =
        showHeading || location.pathname === "/hotels";

    // SEARCH DEBOUNCE
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
        }, 400);

        return () => clearTimeout(timer);
    }, [search]);

    // WISHLIST SYNC
    useEffect(() => {
        const sync = () => setWishlist(readWishlist());

        window.addEventListener(
            "hotel-wishlist-updated",
            sync
        );

        window.addEventListener("storage", sync);

        return () => {
            window.removeEventListener(
                "hotel-wishlist-updated",
                sync
            );

            window.removeEventListener("storage", sync);
        };
    }, []);

    // FETCH DESTINATIONS
    useEffect(() => {
        const controller = new AbortController();

        axios
            .get(`${API_URL}/destinations`, {
                signal: controller.signal,
            })
            .then((response) => {
                if (!controller.signal.aborted) {
                    setDestinations(getArray(response.data));
                }
            })
            .catch((err) => {
                if (!controller.signal.aborted) {
                    console.error("Destinations API:", err);
                }
            });

        return () => controller.abort();
    }, []);

    // FETCH HOTELS
    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError("");

        axios
            .get(`${API_URL}/hotels`, {
                signal: controller.signal,
                params: {
                    search: debouncedSearch,
                    destinations: selectedDestinations.map(
                        (destination) => destination.id
                    ),
                    minPrice,
                    maxPrice,
                    rating,
                },
            })
            .then((response) => {
                if (!controller.signal.aborted) {
                    setHotels(getArray(response.data));
                }
            })
            .catch((err) => {
                if (!controller.signal.aborted) {
                    console.error("Hotels API:", err);
                    setError(
                        "We couldn't load hotels. Please try again."
                    );
                    setHotels([]);
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [
        debouncedSearch,
        selectedDestinations,
        minPrice,
        maxPrice,
        rating,
        retry,
    ]);

    // DESTINATION TOGGLE
    const toggleDestination = (destination) => {
        setSelectedDestinations((previous) => {
            const exists = previous.some(
                (item) =>
                    String(item.id) ===
                    String(destination.id)
            );

            return exists
                ? previous.filter(
                    (item) =>
                        String(item.id) !==
                        String(destination.id)
                )
                : [...previous, destination];
        });
    };

    // RESET FILTERS
    const clearFilters = () => {
        setSearch("");
        setDebouncedSearch("");
        setMinPrice(0);
        setMaxPrice(5000);
        setRating(0);
        setSelectedDestinations([]);
    };

    // WISHLIST
    const toggleWishlist = (hotel) => {
        const current = readWishlist();

        const exists = current.some((item) =>
            matchesHotel(item, hotel)
        );

        const next = exists
            ? current.filter(
                (item) => !matchesHotel(item, hotel)
            )
            : [
                ...current,
                {
                    id: hotelKey(hotel),
                    slug: hotel.slug,
                    name: hotelName(hotel),
                    image:
                        hotel.image ||
                        hotel.featured_image ||
                        "",
                    location: hotelLocation(hotel),
                    price_per_night: hotelPrice(hotel),
                    rating: hotelRating(hotel),
                },
            ];

        try {
            localStorage.setItem(
                WISHLIST_KEY,
                JSON.stringify(next)
            );

            const legacy = readStored(LEGACY_KEY).filter(
                (item) => !matchesHotel(item, hotel)
            );

            localStorage.setItem(
                LEGACY_KEY,
                JSON.stringify(legacy)
            );

            setWishlist(next);

            window.dispatchEvent(
                new Event("hotel-wishlist-updated")
            );
        } catch (err) {
            console.error("Wishlist storage:", err);
        }
    };

    // SORT AND LIMIT
    const visibleHotels = useMemo(() => {
        const result = [...hotels];

        if (sort === "price-low") {
            result.sort(
                (a, b) =>
                    (hotelPrice(a) ?? Infinity) -
                    (hotelPrice(b) ?? Infinity)
            );
        }

        if (sort === "price-high") {
            result.sort(
                (a, b) =>
                    (hotelPrice(b) ?? -Infinity) -
                    (hotelPrice(a) ?? -Infinity)
            );
        }

        if (sort === "rating") {
            result.sort(
                (a, b) =>
                    (hotelRating(b) ?? -Infinity) -
                    (hotelRating(a) ?? -Infinity)
            );
        }

        return limit && Number(limit) > 0
            ? result.slice(0, Number(limit))
            : result;
    }, [hotels, limit, sort]);

    const activeFilterCount =
        selectedDestinations.length +
        Number(minPrice > 0) +
        Number(maxPrice < 5000) +
        Number(rating > 0);

    const filterProps = {
        destinations,
        selectedDestinations,
        toggleDestination,
        minPrice,
        setMinPrice,
        maxPrice,
        setMaxPrice,
        rating,
        setRating,
        clearFilters,
    };

    // ========================================
    // RENDER
    // ========================================

    return (
        <div
            className="
        min-h-screen bg-[#F5FAF7]
        pt-24 text-[#17324D]
      "
        >
            {/* HERO BANNER */}
            {showBanner && (
                <section
                    className="
            relative isolate overflow-hidden
            bg-[#064E3B]
          "
                >
                    <img
                        src={hotelImg}
                        alt=""
                        className="
              absolute inset-0 -z-20
              h-full w-full object-cover
            "
                    />

                    <div
                        className="
              absolute inset-0 -z-10
              bg-gradient-to-r
              from-[#043C2C]/95
              via-[#064E3B]/85
              to-[#064E3B]/45
            "
                    />

                    <div
                        className="
              mx-auto w-full max-w-[1536px]
              px-4 py-14
              sm:px-6 sm:py-16
              lg:px-8 lg:py-20
            "
                    >
                        <p
                            className="
                mb-4 flex items-center gap-2
                text-[11px] font-extrabold
                uppercase tracking-[0.2em]
                text-[#86E5A2]
              "
                        >
                            <Sparkles size={16} />
                            Explore Dubai Hotels
                        </p>

                        <h1
                            className="
                max-w-[720px]
                text-[36px] font-extrabold
                leading-[1.15]
                tracking-[-0.04em]
                text-white
                sm:text-[48px]
                lg:text-[60px]
              "
                        >
                            Find Your Perfect{" "}
                            <span className="text-[#86E5A2]">
                                Dubai Stay.
                            </span>
                        </h1>

                        <p
                            className="
                mt-5 max-w-[570px]
                text-[14px] leading-[1.9]
                text-white/80
                sm:text-[15px]
              "
                        >
                            Discover hotels across Dubai, compare
                            locations and accommodation options,
                            and choose a stay that fits your plans.
                        </p>

                        <div
                            className="
                mt-7 flex flex-wrap
                items-center gap-5
                text-[12px] text-white/85
              "
                        >
                            <span className="flex items-center gap-2">
                                <Check
                                    size={16}
                                    className="text-[#86E5A2]"
                                />
                                Explore Hotels
                            </span>

                            <span className="flex items-center gap-2">
                                <Check
                                    size={16}
                                    className="text-[#86E5A2]"
                                />
                                Compare Locations
                            </span>

                            <span className="flex items-center gap-2">
                                <ShieldCheck
                                    size={16}
                                    className="text-[#86E5A2]"
                                />
                                Find Your Stay
                            </span>
                        </div>
                    </div>
                </section>
            )}

            {/* MAIN CONTENT */}
            <main
                id="hotel-results"
                className="
          mx-auto w-full max-w-[1536px]
          px-4 py-10
          sm:px-6 sm:py-12
          lg:px-8 lg:py-14
        "
            >
                {/* PAGE HEADING */}
                {shouldShowHeading && (
                    <div
                        className="
              mb-8 flex flex-wrap
              items-end justify-between gap-5
            "
                    >
                        <div>
                            <p
                                className="
                  mb-2 text-[10px]
                  font-extrabold uppercase
                  tracking-[0.2em]
                  text-[#168B43]
                "
                            >
                                Discover Your Next Stay
                            </p>

                            <h2
                                className="
                  text-[30px] font-extrabold
                  leading-tight
                  tracking-[-0.04em]
                  text-[#17324D]
                  sm:text-[40px]
                "
                            >
                                Explore{" "}
                                <span className="text-[#168B43]">
                                    Dubai Hotels
                                </span>
                            </h2>

                            <p
                                className="
                  mt-3 text-[13px]
                  leading-6 text-[#64748B]
                "
                            >
                                Browse hotels and find the perfect
                                accommodation for your Dubai trip.
                            </p>
                        </div>

                        {/* HOTEL COUNT */}
                        <div
                            className="
                flex items-center gap-3
                rounded-[14px]
                border border-[#DDEBE2]
                bg-white px-5 py-3
              "
                        >
                            <div
                                className="
                  flex h-11 w-11
                  items-center justify-center
                  rounded-[11px]
                  bg-[#E8F7EE]
                "
                            >
                                <Hotel
                                    size={21}
                                    className="text-[#168B43]"
                                />
                            </div>

                            <div>
                                <p
                                    className="
                    text-[20px] font-extrabold
                    text-[#17324D]
                  "
                                >
                                    {loading ? "—" : hotels.length}
                                </p>

                                <p
                                    className="
                    text-[10px] font-bold
                    uppercase tracking-wide
                    text-[#94A3B8]
                  "
                                >
                                    Hotels Found
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* SEARCH TOOLBAR */}
                <div
                    className="
            mb-7 flex flex-wrap gap-3
            rounded-[16px]
            border border-[#E2ECE6]
            bg-white p-3
            shadow-[0_5px_20px_rgba(6,78,59,0.04)]
            sm:p-4
          "
                >
                    {/* SEARCH INPUT */}
                    <div
                        className="
              flex min-w-[200px] flex-1
              items-center gap-3
              rounded-[10px]
              border border-[#E5EFE8]
              bg-[#F8FBF9] px-4
              focus-within:border-[#168B43]
            "
                    >
                        <Search
                            size={19}
                            className="shrink-0 text-[#168B43]"
                        />

                        <input
                            type="search"
                            value={search}
                            onChange={(e) =>
                                setSearch(e.target.value)
                            }
                            placeholder="Search hotels, areas or destinations..."
                            aria-label="Search hotels"
                            className="
                min-w-0 w-full bg-transparent
                py-3.5 text-[13px]
                text-[#17324D] outline-none
                placeholder:text-[#94A3B8]
              "
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch("")}
                                aria-label="Clear search"
                            >
                                <X
                                    size={17}
                                    className="text-[#64748B]"
                                />
                            </button>
                        )}
                    </div>

                    {/* SORT */}
                    <div
                        className="
              relative flex min-w-[190px]
              items-center rounded-[10px]
              border border-[#E5EFE8]
              bg-white
            "
                    >
                        <span
                            className="
                pointer-events-none absolute
                left-3 text-[11px]
                font-semibold text-[#94A3B8]
              "
                        >
                            Sort:
                        </span>

                        <select
                            value={sort}
                            onChange={(e) =>
                                setSort(e.target.value)
                            }
                            aria-label="Sort hotels"
                            className="
                w-full appearance-none
                bg-transparent py-3.5
                pl-12 pr-9
                text-[12px] font-bold
                text-[#17324D]
                outline-none
              "
                        >
                            <option value="recommended">
                                Recommended
                            </option>

                            <option value="price-low">
                                Price: Low to High
                            </option>

                            <option value="price-high">
                                Price: High to Low
                            </option>

                            <option value="rating">
                                Highest Rated
                            </option>
                        </select>

                        <ChevronDown
                            size={15}
                            className="
                pointer-events-none
                absolute right-3
                text-[#168B43]
              "
                        />
                    </div>

                    {/* MOBILE FILTER BUTTON */}
                    {showSidebar && (
                        <button
                            type="button"
                            onClick={() =>
                                setMobileFiltersOpen(true)
                            }
                            className="
                inline-flex items-center
                justify-center gap-2
                rounded-[10px]
                bg-[#168B43]
                px-4 py-3
                text-[12px] font-bold
                text-white
                lg:hidden
              "
                        >
                            <SlidersHorizontal size={16} />
                            Filters

                            {activeFilterCount > 0 && (
                                <span
                                    className="
                    rounded-full bg-white
                    px-2 py-0.5
                    text-[10px] font-extrabold
                    text-[#168B43]
                  "
                                >
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                    )}
                </div>

                {/* SIDEBAR + RESULTS */}
                <div
                    className={`
            grid items-start gap-7
            ${showSidebar
                            ? "lg:grid-cols-[260px_minmax(0,1fr)] xl:grid-cols-[285px_minmax(0,1fr)]"
                            : ""
                        }
          `}
                >
                    {/* DESKTOP SIDEBAR */}
                    {showSidebar && (
                        <div
                            className="
                hidden lg:sticky
                lg:top-28 lg:block
              "
                        >
                            <FilterPanel {...filterProps} />
                        </div>
                    )}

                    {/* RESULTS */}
                    <div className="min-w-0">
                        {/* RESULTS HEADER */}
                        <div
                            className="
                mb-5 flex flex-wrap
                items-center justify-between
                gap-3
              "
                        >
                            <div>
                                <h3
                                    className="
                    text-[19px] font-extrabold
                    text-[#17324D]
                  "
                                >
                                    {search
                                        ? "Search Results"
                                        : "Available Hotels"}
                                </h3>

                                <p
                                    className="
                    mt-1 text-[12px]
                    text-[#64748B]
                  "
                                >
                                    {loading
                                        ? "Loading hotels..."
                                        : `${visibleHotels.length} ${visibleHotels.length === 1
                                            ? "property"
                                            : "properties"
                                        } displayed`}
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                {activeFilterCount > 0 && (
                                    <button
                                        type="button"
                                        onClick={clearFilters}
                                        className="
                      flex items-center gap-1.5
                      text-[12px] font-bold
                      text-[#168B43]
                    "
                                    >
                                        <RotateCcw size={14} />
                                        Clear Filters
                                    </button>
                                )}

                                {/* GRID / LIST */}
                                <div
                                    className="
                    flex rounded-[10px]
                    border border-[#E2ECE6]
                    bg-white p-1
                  "
                                >
                                    <button
                                        type="button"
                                        onClick={() => setView("grid")}
                                        aria-label="Grid view"
                                        aria-pressed={view === "grid"}
                                        className={`
                      rounded-[8px] p-2.5
                      transition
                      ${view === "grid"
                                                ? "bg-[#168B43] text-white"
                                                : "text-[#94A3B8] hover:text-[#168B43]"
                                            }
                    `}
                                    >
                                        <LayoutGrid size={17} />
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setView("list")}
                                        aria-label="List view"
                                        aria-pressed={view === "list"}
                                        className={`
                      rounded-[8px] p-2.5
                      transition
                      ${view === "list"
                                                ? "bg-[#168B43] text-white"
                                                : "text-[#94A3B8] hover:text-[#168B43]"
                                            }
                    `}
                                    >
                                        <ListIcon size={17} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* ERROR STATE */}
                        {error ? (
                            <div
                                className="
                  rounded-[18px]
                  border border-[#E2ECE6]
                  bg-white px-6 py-16
                  text-center
                "
                            >
                                <ImageOff
                                    size={36}
                                    className="
                    mx-auto text-[#168B43]
                  "
                                />

                                <h3
                                    className="
                    mt-4 text-[22px]
                    font-extrabold text-[#17324D]
                  "
                                >
                                    Unable to Load Hotels
                                </h3>

                                <p
                                    className="
                    mt-2 text-[13px]
                    text-[#64748B]
                  "
                                >
                                    {error}
                                </p>

                                <button
                                    type="button"
                                    onClick={() =>
                                        setRetry((value) => value + 1)
                                    }
                                    className="
                    mt-6 rounded-[10px]
                    bg-[#168B43]
                    px-6 py-3
                    text-[13px] font-bold
                    text-white
                    hover:bg-[#087D37]
                  "
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : loading ? (
                            /* LOADING SKELETON */
                            <div
                                className={`
                  grid gap-5
                  ${view === "grid"
                                        ? "md:grid-cols-2"
                                        : "grid-cols-1"
                                    }
                `}
                            >
                                {Array.from({
                                    length: Math.min(
                                        Number(limit) || 4,
                                        4
                                    ),
                                }).map((_, index) => (
                                    <div
                                        key={index}
                                        className="
                      overflow-hidden rounded-[18px]
                      border border-[#E2ECE6]
                      bg-white
                    "
                                    >
                                        <div
                                            className="
                        h-[230px] animate-pulse
                        bg-[#E2EFE7]
                      "
                                        />

                                        <div className="space-y-3 p-6">
                                            <div
                                                className="
                          h-4 w-2/3
                          animate-pulse rounded
                          bg-[#E2EFE7]
                        "
                                            />

                                            <div
                                                className="
                          h-4 w-full
                          animate-pulse rounded
                          bg-[#F0F6F2]
                        "
                                            />

                                            <div
                                                className="
                          h-12 animate-pulse
                          rounded bg-[#F0F6F2]
                        "
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : visibleHotels.length > 0 ? (
                            /* HOTEL CARDS */
                            <div
                                className={`
                  grid gap-5
                  ${view === "grid"
                                        ? "md:grid-cols-2"
                                        : "grid-cols-1"
                                    }
                `}
                            >
                                {visibleHotels.map((hotel, index) => (
                                    <HotelCard
                                        key={
                                            hotel.id ??
                                            hotel.slug ??
                                            index
                                        }
                                        hotel={hotel}
                                        saved={wishlist.some((item) =>
                                            matchesHotel(item, hotel)
                                        )}
                                        onWishlist={toggleWishlist}
                                        view={view}
                                    />
                                ))}
                            </div>
                        ) : (
                            /* EMPTY STATE */
                            <div
                                className="
                  rounded-[18px]
                  border border-[#E2ECE6]
                  bg-white px-6 py-16
                  text-center
                "
                            >
                                <div
                                    className="
                    mx-auto flex h-16 w-16
                    items-center justify-center
                    rounded-full bg-[#E8F7EE]
                  "
                                >
                                    <Search
                                        size={28}
                                        className="text-[#168B43]"
                                    />
                                </div>

                                <h3
                                    className="
                    mt-5 text-[22px]
                    font-extrabold
                    text-[#17324D]
                  "
                                >
                                    No Hotels Found
                                </h3>

                                <p
                                    className="
                    mx-auto mt-3 max-w-md
                    text-[13px] leading-6
                    text-[#64748B]
                  "
                                >
                                    Try changing your search or
                                    resetting your filters to
                                    explore more hotel options.
                                </p>

                                <button
                                    type="button"
                                    onClick={clearFilters}
                                    className="
                    mt-6 rounded-[10px]
                    bg-[#168B43]
                    px-6 py-3
                    text-[13px] font-bold
                    text-white
                    hover:bg-[#087D37]
                  "
                                >
                                    Reset Filters
                                </button>
                            </div>
                        )}

                        {/* VIEW ALL BUTTON */}
                        {!loading &&
                            !error &&
                            visibleHotels.length > 0 &&
                            showViewAllButton &&
                            location.pathname !== "/hotels" && (
                                <div className="mt-9 text-center">
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate("/hotels")
                                        }
                                        className="
                      inline-flex items-center gap-2
                      rounded-[10px]
                      border border-[#168B43]
                      bg-white px-6 py-3.5
                      text-[13px] font-bold
                      text-[#168B43]
                      transition
                      hover:bg-[#168B43]
                      hover:text-white
                    "
                                    >
                                        View All Hotels
                                        <ArrowRight size={16} />
                                    </button>
                                </div>
                            )}
                    </div>
                </div>
            </main>

            {/* MOBILE FILTER DRAWER */}
            {showSidebar && mobileFiltersOpen && (
                <div
                    className="
            fixed inset-0 z-[100]
            lg:hidden
          "
                >
                    <button
                        type="button"
                        aria-label="Close filter drawer"
                        onClick={() =>
                            setMobileFiltersOpen(false)
                        }
                        className="
              absolute inset-0
              bg-[#052E22]/70
            "
                    />

                    <div
                        className="
              absolute inset-y-0 right-0
              w-[90%] max-w-[380px]
              overflow-y-auto
              bg-[#F5FAF7] p-3
              shadow-2xl
            "
                    >
                        <FilterPanel
                            {...filterProps}
                            onClose={() =>
                                setMobileFiltersOpen(false)
                            }
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
